import { instrumentRunQueryKeys, orgQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import {
  INSTRUMENT_RUN_ACTIVE_POLL_INTERVAL_MS,
  INSTRUMENT_RUN_COMPLETED_STALE_TIME,
  INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
  INSTRUMENT_RUN_GC_TIME,
  STUDIES_QUERY_GC_TIME,
  STUDIES_QUERY_STALE_TIME,
} from '@/constants';
import InstrumentRunConsumables, {
  type InstrumentRunConsumablesHandle,
} from '@/pages/Instrument_Runs/components/InstrumentRunConsumables';
import InstrumentRunHeader from '@/pages/Instrument_Runs/components/InstrumentRunHeader/InstrumentRunHeader';
import { RunActionRow } from '@/pages/Instrument_Runs/components/RunActionRow';
import RunControlsAndSamples from '@/pages/Instrument_Runs/components/RunControlsAndSamples';
import { RunEditForm, type RunEditFormHandle } from '@/pages/Instrument_Runs/components/RunEditForm';
import RunSampleDetailsSection from '@/pages/Instrument_Runs/components/RunSampleDetailsSection';
import { isInstrumentRunActive } from '@/pages/Instrument_Runs/runStatus';
import type { InstrumentRunForm } from '@/pages/Instrument_Runs/types';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { fetchInstrumentRun } from '@/services/instrumentRuns';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunPublic, Org, PaginatedStudiesResponse } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import React, { createElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import irStyles from '../../pages/Instrument_Runs/styles.module.css';

const instrumentRunSearchSchema = z.object({
  edit: z.coerce.boolean().optional(),
});

export const Route = createFileRoute('/instrument_runs/$instrumentRunId')({
  beforeLoad: ({ context }) => {
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({ to: '/login' });
    }
  },
  loader: async ({ context, params }) => {
    const store = useAppStore.getState();
    const selectedOrg = store.selectedOrganization ?? store.selectedOrgStub;

    if (!selectedOrg?.id) {
      throw redirect({ to: '/organizations' });
    }

    const instrumentRun = await context.queryClient.fetchQuery<InstrumentRunPublic>({
      queryKey: instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId),
      queryFn: () => fetchInstrumentRun(selectedOrg.id, params.instrumentRunId),
      staleTime: 0,
      gcTime: INSTRUMENT_RUN_GC_TIME,
    });

    if (instrumentRun?.status === 'submitted') {
      context.queryClient.setQueryDefaults(instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId), {
        staleTime: INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
        gcTime: INSTRUMENT_RUN_GC_TIME,
      });
    } else if (instrumentRun?.status === 'completed') {
      context.queryClient.setQueryDefaults(instrumentRunQueryKeys.detail(selectedOrg.id, params.instrumentRunId), {
        staleTime: INSTRUMENT_RUN_COMPLETED_STALE_TIME,
        gcTime: INSTRUMENT_RUN_GC_TIME,
      });
    }

    // Draft runs open straight into edit mode, so prefetch studies/organizations up front.
    // Non-draft runs fetch these lazily in-component when edit is toggled (see useQuery calls
    // below) — doing it here via loaderDeps on `edit` would re-run this whole loader (including
    // the instrumentRun fetch above) on every edit toggle, triggering the route's full-page
    // pendingComponent even though the run data is already cached.
    if (instrumentRun?.status === 'draft') {
      try {
        await context.queryClient.prefetchQuery<Org[]>({
          queryKey: orgQueryKeys.lists(),
          queryFn: fetchOrganizations,
        });
      } catch {
        // prefetch failed — component will fetch on mount
      }

      try {
        await context.queryClient.prefetchQuery<PaginatedStudiesResponse>({
          queryKey: studyQueryKeys.list(selectedOrg.id),
          queryFn: async () =>
            searchStudies(selectedOrg.id, {
              skip: 0,
              limit: 500,
              sort_by: 'created_at',
              sort_order: 'desc',
            }),
          staleTime: STUDIES_QUERY_STALE_TIME,
          gcTime: STUDIES_QUERY_GC_TIME,
        });
      } catch {
        // prefetch failed — component will fetch on mount
      }
    }

    return { instrumentRun, orgId: selectedOrg.id };
  },
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: PendingComponent,
  validateSearch: instrumentRunSearchSchema,
});

function PendingComponent() {
  return createElement(FullLoading, { addEllipsis: true, label: 'Loading run' });
}

function RouteComponent() {
  const { t } = useTranslation('runs');
  const navigate = useNavigate();
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));
  const { instrumentRunId } = Route.useParams();
  const { edit } = Route.useSearch();
  const { instrumentRun: _loaderRun, orgId } = Route.useLoaderData();
  const selectedOrgId = orgId ?? selectedOrg?.id ?? '';

  const { data } = useSuspenseQuery<InstrumentRunPublic>({
    queryKey: instrumentRunQueryKeys.detail(selectedOrgId, instrumentRunId),
    queryFn: () => fetchInstrumentRun(selectedOrgId, instrumentRunId),
    staleTime: INSTRUMENT_RUN_SUBMITTED_STALE_TIME,
    gcTime: INSTRUMENT_RUN_GC_TIME,
    // Decode status/QC arrive asynchronously with no push channel — poll while the
    // instrument or decode job is still in progress so an open tab stays current.
    refetchInterval: query =>
      query.state.data && isInstrumentRunActive(query.state.data) ? INSTRUMENT_RUN_ACTIVE_POLL_INTERVAL_MS : false,
  });

  const isEditing = !!edit;
  const [editForm, setEditForm] = useState<InstrumentRunForm | undefined>(undefined);
  const formRef = useRef<RunEditFormHandle>(null);
  const consumablesRef = useRef<InstrumentRunConsumablesHandle>(null);

  // Callback ref: captures the form instance when RunEditForm mounts
  const handleFormRef = useCallback((handle: RunEditFormHandle | null) => {
    (formRef as React.MutableRefObject<RunEditFormHandle | null>).current = handle;
    setEditForm(handle?.form);
  }, []);

  const handleCancel = useCallback(() => {
    void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId }, search: {} });
    setEditForm(undefined);
  }, [navigate, instrumentRunId]);

  const handleSave = useCallback(() => {
    void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId }, search: {} });
    setEditForm(undefined);
  }, [navigate, instrumentRunId]);

  const { data: organizationsData } = useQuery<Org[]>({
    queryKey: orgQueryKeys.lists(),
    queryFn: fetchOrganizations,
    enabled: isEditing,
  });
  const organizations = organizationsData ?? [];

  const { data: studiesData } = useQuery<PaginatedStudiesResponse>({
    queryKey: studyQueryKeys.list(selectedOrgId),
    queryFn: async () =>
      searchStudies(selectedOrgId, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      }),
    enabled: isEditing,
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
  });

  const studies = useMemo(() => {
    const d = studiesData as PaginatedStudiesResponse | undefined;
    if (!d || !Array.isArray(d.records)) return [];
    return d.records;
  }, [studiesData]);

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error('No organization selected')} />;
  }

  if (!data) {
    return <Text>{t('empty', { ns: 'general' })}</Text>;
  }

  return (
    <Stack className={irStyles.contentContainer} fullHeight fullWidth gap={3} orientation="vertical">
      <RunActionRow
        isEditing={isEditing}
        onCancel={handleCancel}
        onEdit={() =>
          void navigate({
            to: '/instrument_runs/$instrumentRunId',
            params: { instrumentRunId },
            search: { edit: true },
          })
        }
        onSave={() => {
          formRef.current?.submit();
          consumablesRef.current?.submit();
        }}
        onSubmit={data.status === 'draft' ? () => formRef.current?.submitDraft() : undefined}
        run={data}
      />

      <InstrumentRunHeader form={editForm} isEditing={isEditing} run={data} />

      {/* Consumables PATCHes independently of the main run PUT, but is saved by the page's single
          Save action (below) and editable only in edit mode, same as name/description. */}
      <InstrumentRunConsumables
        ref={consumablesRef}
        isEditing={isEditing}
        orgId={selectedOrgId}
        runId={instrumentRunId}
        values={data.consumables}
      />

      {isEditing ? (
        <RunEditForm
          ref={handleFormRef}
          mode="edit"
          orgId={selectedOrgId}
          organizations={organizations}
          run={data}
          studies={studies}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      ) : (
        <>
          {data.samples?.length > 0 && <RunControlsAndSamples id={data.id} samples={data.samples} />}
          {data.status === 'completed' && <RunSampleDetailsSection orgId={selectedOrgId} run={data} />}
        </>
      )}
    </Stack>
  );
}
